/**
 * The Employment check skill, shared by every surface
 * that shows it: the list Actions drawer, the leads table columns and the
 * play builder's skill picker. It mirrors the API's skill contract
 * (`providers/smart_agents/employment_check/skill.py`): one structured
 * result column per list, split here into twelve sub-field columns.
 */

import { isRecord } from '@/lib/is-record';

export const EMPLOYMENT_CHECK_AGENT_TYPE = 'employment_check';
export const EMPLOYMENT_CHECK_SKILL_ID = 'builtin:employment_check';
export const EMPLOYMENT_CHECK_NAME = 'Employment check';

/** Worst case a person: the API checks the balance against this before a run
 *  and bills each identified row its own charges after the check. */
export const EMPLOYMENT_CHECK_MAX_CREDITS = 3.5;
/** What the skill does, as its siblings in the Actions drawer say theirs. */
export const EMPLOYMENT_CHECK_DESCRIPTION =
  'Check whether each person still works at their company, and where they went.';

export interface EmploymentCheckField {
  key: string;
  label: string;
  /** Display values the API writes, for fields that only take a fixed set. */
  options?: readonly string[];
}

/** `processed_value.structured` keys and their labels, in column order. */
export const EMPLOYMENT_CHECK_FIELDS: readonly EmploymentCheckField[] = [
  {
    key: 'still_at_company',
    label: 'Still at Company?',
    options: ['Yes', 'No', 'Unknown'],
  },
  {
    key: 'confidence',
    label: 'Confidence',
    options: ['High', 'Medium', 'Low'],
  },
  { key: 'current_employer', label: 'Current Employer' },
  { key: 'current_title', label: 'Current Title' },
  { key: 'left_on', label: 'Left On' },
  { key: 'updated_email', label: 'Updated Email' },
  {
    key: 'email_status',
    label: 'Email Status',
    options: ['Valid', 'Invalid', 'Catch-all', 'Unknown'],
  },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'evidence_source', label: 'Evidence Source' },
  { key: 'evidence_url', label: 'Evidence URL' },
  { key: 'last_verified', label: 'Last Verified Date' },
  { key: 'notes', label: 'Notes' },
];

/** The skill's own result column on the leads table. */
export const EMPLOYMENT_CHECK_COLUMN_ID = `smart_${EMPLOYMENT_CHECK_AGENT_TYPE}`;

const FIELD_COLUMN_PREFIX = `smart:${EMPLOYMENT_CHECK_AGENT_TYPE}:`;

/** Sub-field column id. The run writes `column_name: null`, so the id has no
 *  name segment: `smart:employment_check:<key>`. */
export const employmentCheckFieldColumnId = (key: string) =>
  `${FIELD_COLUMN_PREFIX}${key}`;

const FIELD_LABELS = new Map(
  EMPLOYMENT_CHECK_FIELDS.map((field) => [
    employmentCheckFieldColumnId(field.key),
    field.label,
  ])
);

export const isEmploymentCheckFieldColumnId = (columnId: string) =>
  FIELD_LABELS.has(columnId);

/** The result column or one of its sub-field columns. */
export const isEmploymentCheckColumnId = (columnId: string) =>
  columnId === EMPLOYMENT_CHECK_COLUMN_ID ||
  isEmploymentCheckFieldColumnId(columnId);

export const employmentCheckFieldLabel = (columnId: string) =>
  FIELD_LABELS.get(columnId);

const FIELD_OPTIONS = new Map(
  EMPLOYMENT_CHECK_FIELDS.flatMap((field) =>
    field.options
      ? [[employmentCheckFieldColumnId(field.key), field.options] as const]
      : []
  )
);

/** The verdict the result column shows for a row's `processed_value`:
 *  `structured.still_at_company`, or '' while the row has none. */
export function employmentCheckVerdict(value: unknown): string {
  if (!isRecord(value) || !isRecord(value.structured)) return '';
  const verdict = value.structured.still_at_company;
  return typeof verdict === 'string' ? verdict : '';
}

/**
 * The text an Employment check column shows for a row: the verdict for the
 * result column (whose value is the whole `processed_value`), the field's own
 * value for a sub-field column. Sorting and filtering read this, so they act
 * on what the cell shows.
 */
export function employmentCheckCellText(
  columnId: string,
  value: unknown
): string {
  if (columnId === EMPLOYMENT_CHECK_COLUMN_ID) {
    return employmentCheckVerdict(value);
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

/**
 * Where a value sits in its column's fixed set, so Still at Company? sorts
 * Yes, No, Unknown and Confidence sorts High, Medium, Low rather than
 * alphabetically. Undefined for free-text columns and values outside the set.
 */
export function employmentCheckValueRank(
  columnId: string,
  value: string
): number | undefined {
  const options =
    columnId === EMPLOYMENT_CHECK_COLUMN_ID
      ? FIELD_OPTIONS.get(employmentCheckFieldColumnId('still_at_company'))
      : FIELD_OPTIONS.get(columnId);
  if (!options) return undefined;
  const index = options.findIndex(
    (option) => option.toLowerCase() === value.trim().toLowerCase()
  );
  return index === -1 ? undefined : index;
}

/** Explicit order so the result column and its sub-fields sit together after
 *  every other column, in contract order. Saved column configs override it. */
const COLUMN_ORDER_BASE = 3000;

interface TableColumn {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'boolean';
  width: number;
  visible: boolean;
  displayOrder?: number;
}

/**
 * Adds the result column and its twelve sub-field columns to the table's
 * custom columns, skipping any the table already has (hidden ones stay
 * hidden) or that were just deleted. A just-deleted result column adds
 * nothing. Returns `columns` itself when nothing is missing, so a state
 * setter can bail out.
 */
export function withEmploymentCheckColumns<T extends TableColumn>(
  columns: T[],
  skip: ReadonlySet<string> = new Set()
): T[] {
  if (skip.has(EMPLOYMENT_CHECK_COLUMN_ID)) return columns;
  const present = new Set(columns.map((col) => col.id));
  const wanted: TableColumn[] = [
    {
      id: EMPLOYMENT_CHECK_COLUMN_ID,
      label: EMPLOYMENT_CHECK_NAME,
      type: 'text',
      width: 200,
      visible: true,
      displayOrder: COLUMN_ORDER_BASE,
    },
    ...EMPLOYMENT_CHECK_FIELDS.map((field, index) => ({
      id: employmentCheckFieldColumnId(field.key),
      label: field.label,
      type: 'text' as const,
      width: 180,
      visible: true,
      displayOrder: COLUMN_ORDER_BASE + index + 1,
    })),
  ];
  const missing = wanted.filter(
    (col) => !present.has(col.id) && !skip.has(col.id)
  );
  if (missing.length === 0) return columns;
  return [...columns, ...(missing as T[])];
}

/**
 * Why a row failed, for a row whose `processed_value` is null. The API saves
 * the message as the row's `error_message` and in `column_metadata.error`,
 * and a websocket row update carries only the metadata.
 */
export function employmentCheckFailure(
  metadata: Record<string, unknown> | null | undefined,
  errorMessage?: string | null
): string | undefined {
  if (errorMessage && errorMessage.trim()) return errorMessage;
  const fromMetadata = metadata?.error;
  return typeof fromMetadata === 'string' && fromMetadata.trim()
    ? fromMetadata
    : undefined;
}
